import React, { useState, useEffect, useRef } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { Eye, Flame, TrendingUp, Play, BarChart3, Instagram, Youtube, Heart, Clapperboard, MessageCircle } from 'lucide-react';
import { GlassCard } from '../_shared/GlassCard';
import { Footer } from '../_shared/Footer';

/* ----------------------------------------------------------------------------
   DATA — 60-day window Jul 1 - Aug 31, 2026, scraped LIVE 2026-09-07.
   Instagram: Apify (89 posts, all reels). Raw: clients-current/kush/research/
     kush-ig-jul-sep-raw-2026-09-07.json (July subset also in kush-ig-july-full-*.json)
   YouTube: yt-dlp public view counts (89 shorts). Raw: kush-youtube-shorts-2026-09-07.txt
   Plays are public play counts, NOT private reach.
---------------------------------------------------------------------------- */

const IG = { posts: 89, plays: 3399980, likes: 120845, comments: 12063, avg: 38202 };
const YT = { posts: 89, views: 316165, best: 58517 };
const TOTAL_VIDEOS = IG.posts + YT.posts;

/* Videos published (both platforms) per half-month block. */
const RAMP = [
    { block: 'Jul 1-15', videos: 30 },
    { block: 'Jul 16-31', videos: 30 },
    { block: 'Aug 1-15', videos: 56 },
    { block: 'Aug 16-31', videos: 62 },
];

/* Shape of the 89 Instagram reels by play count. */
const IG_DISTRIBUTION = [
    { bucket: '1M+', sub: 'Viral breakouts', count: 2, pct: 2, color: 'from-emerald-500 to-teal-400' },
    { bucket: '100K-1M', sub: 'Big travelers', count: 3, pct: 3, color: 'from-yellow-500 to-amber-400' },
    { bucket: '10K-100K', sub: 'Strong', count: 11, pct: 12, color: 'from-orange-500 to-amber-400' },
    { bucket: '1K-10K', sub: 'Steady baseline', count: 70, pct: 79, color: 'from-blue-500 to-indigo-400' },
    { bucket: 'Under 1K', sub: 'Low travel', count: 3, pct: 3, color: 'from-slate-500 to-slate-400' },
];

const IG_TOP = [
    { hook: 'AI data centers vs your drinking water', plays: '1,087,331', likes: '56,867', comments: '867', url: 'https://www.instagram.com/p/DanPzHkhFys/', gradient: 'from-blue-600 via-indigo-500 to-violet-500', date: 'Jul 10' },
    { hook: '13,000 AI agents opened wallets to survive', plays: '1,072,682', likes: '29,819', comments: '6,595', url: 'https://www.instagram.com/p/Da4XlSfhtNo/', gradient: 'from-emerald-600 via-teal-500 to-cyan-400', date: 'Jul 17' },
    { hook: 'The college student who out-reported the news channels', plays: '283,492', likes: '19,135', comments: '362', url: 'https://www.instagram.com/p/Da-rhw6NJBL/', gradient: 'from-yellow-600 via-amber-500 to-orange-400', date: 'Jul 19' },
    { hook: 'Every vibe-coded app looks the same, and breaks the same', plays: '264,037', likes: '5,922', comments: '425', url: 'https://www.instagram.com/p/Db5YvkYBzn6/', gradient: 'from-purple-600 via-fuchsia-500 to-pink-500', date: 'Aug 11' },
    { hook: 'Why AI-built websites look average, and the fix', plays: '100,280', likes: '868', comments: '130', url: 'https://www.instagram.com/p/Dbh5Nrgho08/', gradient: 'from-red-600 via-rose-500 to-orange-400', date: 'Aug 2' },
];

const YT_TOP = [
    { hook: 'The $31M hotel designed by AI before construction', views: '58,517', url: 'https://www.youtube.com/shorts/6DEUqdVcFKo', gradient: 'from-emerald-600 via-teal-500 to-cyan-400', date: 'Aug 10' },
    { hook: 'China open-sourced an AI that reads a 100-page PDF in one pass', views: '23,017', url: 'https://www.youtube.com/shorts/B5jZXpv4j6E', gradient: 'from-yellow-600 via-amber-500 to-orange-400', date: 'Aug 1' },
    { hook: 'The AI that dies if it does not make money', views: '15,896', url: 'https://www.youtube.com/shorts/zL2GqJp_Tgw', gradient: 'from-blue-600 via-indigo-500 to-violet-500', date: 'Jul 17' },
    { hook: 'AI video dies at 15 seconds, this free model keeps going', views: '13,425', url: 'https://www.youtube.com/shorts/lxS2VaVfcBs', gradient: 'from-purple-600 via-fuchsia-500 to-pink-500', date: 'Aug 18' },
    { hook: 'Launch a real startup without hiring anyone', views: '13,251', url: 'https://www.youtube.com/shorts/SqzKveZMd5E', gradient: 'from-red-600 via-rose-500 to-orange-400', date: 'Jul 25' },
];

/* ---------------------------------------------------------------- count-up hook */
const useCounter = (end, duration = 1400) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);
    useEffect(() => {
        const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    if (reduced) { setCount(end); return; }
                    const start = performance.now();
                    const animate = (now) => {
                        const progress = Math.min(1, (now - start) / duration);
                        const eased = 1 - Math.pow(1 - progress, 3);
                        setCount(Math.floor(end * eased));
                        if (progress < 1) requestAnimationFrame(animate);
                    };
                    requestAnimationFrame(animate);
                }
            },
            { threshold: 0.3 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration]);
    return [count, ref];
};

const KPICard = ({ icon: Icon, iconColor, label, value, subtext }) => {
    const [animatedValue, ref] = useCounter(value);
    return (
        <GlassCard className="flex flex-col justify-between min-h-[170px]">
            <div ref={ref}>
                <div className={`${iconColor} p-3 rounded-xl mb-4 inline-flex`}>
                    <Icon size={22} />
                </div>
                <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-2">{label}</p>
                <p className="text-4xl font-black text-white tracking-tight tabular-nums">
                    {animatedValue.toLocaleString()}
                </p>
                <p className="text-white/50 text-sm mt-3">{subtext}</p>
            </div>
        </GlassCard>
    );
};

const PlatformLabel = ({ icon: Icon, color, name, tagline }) => (
    <div className="text-center mb-10">
        <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border ${color.border} ${color.bg} backdrop-blur-md mb-4`}>
            <Icon size={15} className={color.text} />
            <span className={`text-xs uppercase tracking-widest font-semibold ${color.text}`}>{name}</span>
        </div>
        {tagline && <p className="text-white/50 max-w-2xl mx-auto">{tagline}</p>}
    </div>
);

/* ----------------------------------------------------------------- Hero section */
const Hero = () => (
    <section className="min-h-[80vh] flex flex-col justify-center items-center text-center px-4 pt-24 pb-12 relative z-10">
        <div className="space-y-6 max-w-5xl mx-auto w-full">
            <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/10 backdrop-blur-md mb-2">
                <span className="text-sm font-medium text-yellow-300 tracking-wide uppercase">Jul 1 to Aug 31, 2026 · 60-Day Report · @realkushdesai</span>
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white via-yellow-100 to-white drop-shadow-lg leading-tight">
                178 Videos.<br />3.7 Million Views.
            </h1>

            <p className="text-lg md:text-xl text-yellow-200/70 max-w-2xl mx-auto leading-relaxed">
                Sixty days of daily publishing on <span className="text-white font-semibold">Instagram + YouTube</span>, with output doubled mid-window from 2 to 4 videos a day. Two reels crossed <span className="text-white font-semibold">a million plays</span> each.
            </p>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-12 w-full">
                <KPICard icon={Clapperboard} iconColor="bg-amber-500/20 text-amber-300" label="Videos Published" value={TOTAL_VIDEOS} subtext="89 reels + 89 shorts" />
                <KPICard icon={Eye} iconColor="bg-rose-500/20 text-rose-300" label="Instagram Plays" value={IG.plays} subtext={`${IG.avg.toLocaleString()} avg per reel`} />
                <KPICard icon={Heart} iconColor="bg-pink-500/20 text-pink-300" label="Instagram Likes" value={IG.likes} subtext={`+ ${IG.comments.toLocaleString()} comments`} />
                <KPICard icon={Youtube} iconColor="bg-red-500/20 text-red-300" label="YouTube Views" value={YT.views} subtext="Best short: 58,517" />
            </div>

            <p className="text-white/30 text-xs max-w-2xl mx-auto pt-6">
                Scraped live on Sep 7, 2026. Instagram figures are public play counts, not private reach. YouTube figures are public view counts. Recent posts are still accumulating views.
            </p>
        </div>
    </section>
);

/* ------------------------------------------------------ Output ramp */
const RampTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
        <div className="bg-black/85 backdrop-blur-md border border-white/15 rounded-xl px-4 py-3 text-left">
            <p className="text-white font-bold text-sm mb-1">{d.block}</p>
            <p className="text-yellow-300 text-sm tabular-nums">{d.videos} videos published</p>
        </div>
    );
};

const rampLabel = ({ x, y, width, value }) => (
    <text x={x + width / 2} y={y - 8} fill="#fde68a" fontSize={13} fontWeight={700} textAnchor="middle">{value}</text>
);

const OutputRamp = () => (
    <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-4">
                    <BarChart3 size={14} className="text-yellow-300" />
                    <span className="text-xs uppercase tracking-widest text-white/60 font-semibold">Publishing Volume · Both Platforms</span>
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-3">Output Doubled Mid-Window</h2>
                <p className="text-white/50 max-w-2xl mx-auto">
                    July ran one reel and one short a day. In August the machine went to two of each, every single day, without missing the schedule.
                </p>
            </div>

            <GlassCard>
                <div className="h-[300px] w-full min-h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={RAMP} margin={{ top: 28, right: 10, left: -14, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                            <XAxis dataKey="block" tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 12 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
                            <Tooltip content={<RampTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                            <Bar dataKey="videos" fill="#fbbf24" radius={[6, 6, 0, 0]}>
                                <LabelList dataKey="videos" content={rampLabel} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <p className="text-white/30 text-xs text-center mt-4">Videos published per half-month, Instagram and YouTube combined.</p>
            </GlassCard>
        </div>
    </section>
);

/* ----------------------------------------------------------- Distribution */
const Distribution = () => (
    <section className="py-8 px-4">
        <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">The Shape of 89 Reels</h2>
                <p className="text-white/50 max-w-2xl mx-auto">
                    A hit-driven account: a steady four-figure baseline, and five reels that traveled past 100K, two of them past a million.
                </p>
            </div>
            <GlassCard>
                <div className="space-y-5">
                    {IG_DISTRIBUTION.map((d) => (
                        <div key={d.bucket}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-baseline gap-3">
                                    <span className="text-white font-bold text-sm w-24">{d.bucket}</span>
                                    <span className="text-white/40 text-xs">{d.sub}</span>
                                </div>
                                <span className="text-white/70 text-sm font-mono tabular-nums">{d.count} reels · {d.pct}%</span>
                            </div>
                            <div className="h-3 w-full rounded-full bg-white/5 overflow-hidden">
                                <div className={`h-full rounded-full bg-gradient-to-r ${d.color}`} style={{ width: `${Math.max(d.pct, 2)}%` }} />
                            </div>
                        </div>
                    ))}
                </div>
                <p className="text-white/40 text-xs mt-5 pt-4 border-t border-white/5">Instagram reels in the window, grouped by public play count.</p>
            </GlassCard>
        </div>
    </section>
);

/* ------------------------------------------------------------ reel/video tiles */
const ReelTile = ({ reel, rank, footerSub }) => (
    <a
        href={reel.url}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative block rounded-[2rem] overflow-hidden transition-transform duration-300 hover:scale-[1.03] cursor-pointer"
        style={{ aspectRatio: '9/16' }}
    >
        <div className={`absolute inset-0 bg-gradient-to-br ${reel.gradient}`} />
        <span className="absolute -bottom-6 -right-2 text-[9rem] font-black text-white/10 leading-none select-none">{rank}</span>
        <div className="absolute top-3 left-3">
            <span className="bg-black/40 backdrop-blur-md text-white text-xs font-bold px-2.5 py-1 rounded-full border border-white/10">#{rank}</span>
        </div>
        <div className="absolute top-3 right-3">
            <span className="bg-black/40 backdrop-blur-md text-white/70 text-[10px] font-semibold px-2 py-1 rounded-full border border-white/10">{reel.date}</span>
        </div>
        <div className="absolute top-1/3 left-3 right-3">
            <div className="bg-black/60 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10">
                <p className="text-white text-xs sm:text-sm font-bold leading-tight text-center">{reel.hook}</p>
            </div>
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 group-hover:backdrop-blur-sm transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center">
                    <Play size={24} className="text-white ml-1" fill="white" />
                </div>
            </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white text-sm font-bold tabular-nums">{reel.plays || reel.views}</p>
            {footerSub && <p className="text-white/50 text-[11px] font-mono">{footerSub(reel)}</p>}
        </div>
    </a>
);

const IGTopReels = () => (
    <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
            <PlatformLabel
                icon={Instagram}
                color={{ border: 'border-rose-500/30', bg: 'bg-rose-500/10', text: 'text-rose-300' }}
                name="Instagram · Top 5 Reels"
                tagline="The window's biggest travelers. Tap any card to watch."
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {IG_TOP.map((reel, i) => <ReelTile key={i} reel={reel} rank={i + 1} footerSub={(r) => `${r.likes} likes · ${r.comments} comments`} />)}
            </div>
        </div>
    </section>
);

const YouTubeSection = () => (
    <section className="py-8 px-4">
        <div className="container mx-auto max-w-6xl">
            <PlatformLabel
                icon={Youtube}
                color={{ border: 'border-red-500/30', bg: 'bg-red-500/10', text: 'text-red-300' }}
                name="YouTube · Top 5 Shorts"
                tagline="The second daily surface kept compounding: 316,165 views across 89 shorts, led by the $31M AI hotel story."
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {YT_TOP.map((reel, i) => <ReelTile key={i} reel={reel} rank={i + 1} />)}
            </div>
        </div>
    </section>
);

/* ----------------------------------------------------------- Highlights */
const Highlights = () => (
    <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <GlassCard className="text-center">
                    <p className="text-3xl font-black text-yellow-300 mb-2 tabular-nums">2 × 1M+</p>
                    <p className="text-white/70 text-sm leading-relaxed">Two reels crossed a million plays inside the window: the data-center water story (1.09M) and the Automaton story (1.07M).</p>
                </GlassCard>
                <GlassCard className="text-center">
                    <p className="text-3xl font-black text-yellow-300 mb-2 tabular-nums">6,595</p>
                    <p className="text-white/70 text-sm leading-relaxed">Comments on the Automaton reel alone, out of 12,063 across the window. The comment CTA keeps converting viewers into conversations.</p>
                </GlassCard>
                <GlassCard className="text-center">
                    <p className="text-3xl font-black text-yellow-300 mb-2 tabular-nums">120,845</p>
                    <p className="text-white/70 text-sm leading-relaxed">Instagram likes across 89 reels, with the top reel alone collecting 56,867.</p>
                </GlassCard>
            </div>
        </div>
    </section>
);

/* ----------------------------------------------------------- Learnings */
const Learnings = () => {
    const items = [
        {
            title: 'Stories with stakes win',
            body: 'Both million-play reels are stories about the world: data centers competing with your tap water, and AI agents that must earn or die. The $31M AI hotel led YouTube the same way. Every new script starts from a story, never from a feature list.',
        },
        {
            title: 'Sharp opinions travel',
            body: 'The August breakout was a take, not a tool: "every vibe-coded app looks the same, and breaks the same" did 264K plays, and its follow-up on AI-built websites cleared 100K. Confident, specific opinions on AI building are now a core lane.',
        },
        {
            title: 'The comment engine works',
            body: 'The Automaton reel pulled 6,595 comments, and on the "get paid when AI waits" reel comments outran likes: 1,900 against 1,354. The comment CTA turns viewers into conversations, and conversations into reach.',
        },
    ];
    return (
        <section className="py-8 pb-16 px-4">
            <div className="container mx-auto max-w-5xl">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 text-center">What the Window Taught Us</h2>
                <p className="text-white/50 text-center mb-10 max-w-2xl mx-auto">Every video is scored after posting and the lessons feed straight into the next batch of scripts.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {items.map((it, i) => (
                        <GlassCard key={i}>
                            <div className="flex items-center gap-2 mb-3">
                                <Flame size={16} className="text-yellow-300" />
                                <p className="text-lg font-bold text-white">{it.title}</p>
                            </div>
                            <p className="text-white/70 text-sm leading-relaxed">{it.body}</p>
                        </GlassCard>
                    ))}
                </div>
            </div>
        </section>
    );
};

/* ----------------------------------------------------------------- Page shell */
export default function Kush60Day() {
    useEffect(() => {
        const pageTitle = 'Kush 60-Day Content Report | SocialSEO';
        document.title = pageTitle;
        const ensureMeta = (selector, attributes) => {
            let el = document.querySelector(selector);
            if (!el) {
                el = document.createElement('meta');
                Object.entries(attributes).forEach(([k, v]) => el.setAttribute(k, v));
                document.head.appendChild(el);
            }
            el.setAttribute('content', pageTitle);
        };
        ensureMeta('meta[property="og:title"]', { property: 'og:title' });
        ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title' });
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white relative" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
            `}</style>

            <header className="fixed top-0 left-0 right-0 z-50 p-4 md:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                    <span className="font-bold tracking-tight text-yellow-300">
                        <span className="text-white">Social</span>SEO
                    </span>
                </div>
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                    <span className="text-sm font-medium text-white/80">Last 60 Days</span>
                </div>
            </header>

            <div className="relative z-10">
                <main>
                    <Hero />
                    <OutputRamp />
                    <Distribution />
                    <IGTopReels />
                    <YouTubeSection />
                    <Highlights />
                    <Learnings />
                </main>
                <Footer />
            </div>
        </div>
    );
}
